import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Button, Card } from 'react-bootstrap';
import { FaTrash, FaSync } from 'react-icons/fa';
import Message from '../components/Message';
import PageHeader from '../components/PageHeader';
import { removeFromCart, updateCartItem } from '../slices/cartSlice';
import { useLazyGetProductDetailsQuery } from '../slices/productsApiSlice';
import { useState } from 'react';

const CartScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;
  const [refreshingIsbn, setRefreshingIsbn] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [triggerGetProductDetails] = useLazyGetProductDetailsQuery();

  const removeFromCartHandler = (id) => {
    if (window.confirm('Weet u zeker dat u dit boek wilt verwijderen?')) {
      dispatch(removeFromCart(id));
    }
  };

  const isStaleItem = (item) => {
    const timestamp = item.lastRefreshedAt || item.updatedAt || item.addedAt || item.createdAt;
    if (!timestamp) return true;
    return Date.now() - Number(timestamp) > 48 * 60 * 60 * 1000;
  };

  const isValidPrice = (item) => Number(item.offer_price) > 0;

  const getItemStatus = (item) => {
    if (!isValidPrice(item)) return 'red';
    if (isStaleItem(item)) return 'orange';
    return 'green';
  };

  const redItems = cartItems.filter((item) => getItemStatus(item) === 'red');
  const orangeItems = cartItems.filter((item) => getItemStatus(item) === 'orange');
  const greenItems = cartItems.filter((item) => getItemStatus(item) === 'green');
  const hasPendingItems = redItems.length > 0 || orangeItems.length > 0;

  const checkoutHandler = () => {
    if (hasPendingItems) {
      setFeedback('Vernieuw of verwijder de items uit de rode en oranje lijst voordat je verder gaat.');
      return;
    }
    navigate('/placeorder');
  };

  const refreshItemHandler = async (item) => {
    setRefreshingIsbn(item.isbn);
    setFeedback('');

    try {
      const result = await triggerGetProductDetails(item.isbn).unwrap();
      if (result?.offer_price > 0) {
        dispatch(updateCartItem({
          isbn: item.isbn,
          offer_price: Number(result.offer_price),
          title: result.title || item.title,
          author: result.author || item.author,
          lastRefreshedAt: Date.now(),
          updatedAt: Date.now(),
        }));
        setFeedback(`${item.isbn} is vernieuwd.`);
      } else {
        dispatch(updateCartItem({
          isbn: item.isbn,
          offer_price: 0,
          lastRefreshedAt: Date.now(),
          updatedAt: Date.now(),
        }));
        setFeedback(`Geen prijs gevonden voor ${item.isbn}.`);
      }
    } catch (error) {
      console.error('Failed to refresh cart item:', error);
      dispatch(updateCartItem({
        isbn: item.isbn,
        offer_price: 0,
        lastRefreshedAt: Date.now(),
        updatedAt: Date.now(),
      }));
      setFeedback(`Geen prijs gevonden voor ${item.isbn}.`);
    } finally {
      setRefreshingIsbn(null);
    }
  };

  const refreshAllStaleItemsHandler = async () => {
    if (orangeItems.length === 0) return;

    setRefreshingAll(true);
    setFeedback('');

    try {
      for (const item of orangeItems) {
        try {
          const result = await triggerGetProductDetails(item.isbn).unwrap();
          if (result?.offer_price > 0) {
            dispatch(updateCartItem({
              isbn: item.isbn,
              offer_price: Number(result.offer_price),
              title: result.title || item.title,
              author: result.author || item.author,
              lastRefreshedAt: Date.now(),
              updatedAt: Date.now(),
            }));
          } else {
            dispatch(updateCartItem({
              isbn: item.isbn,
              offer_price: 0,
              lastRefreshedAt: Date.now(),
              updatedAt: Date.now(),
            }));
          }
        } catch (error) {
          console.error('Failed to refresh cart item:', error);
          dispatch(updateCartItem({
            isbn: item.isbn,
            offer_price: 0,
            lastRefreshedAt: Date.now(),
            updatedAt: Date.now(),
          }));
        }
      }
      setFeedback('Alle oranje items zijn gecontroleerd.');
    } finally {
      setRefreshingAll(false);
    }
  };

  const totalPrice = cartItems
    .reduce((acc, item) => acc + Number(item.offer_price || 0), 0)
    .toFixed(2);

  const renderItemRows = (items, showRefreshButton = true) =>
    items.map((item) => (
      <ListGroup.Item key={item.isbn}>
        <Row className="align-items-center">
          <Col md={3}>
            <strong>{item.isbn}</strong>
          </Col>
          <Col md={4}>{item.title}</Col>
          <Col md={2}>{item.author}</Col>
          <Col md={2}>€ {Number(item.offer_price || 0).toFixed(2)}</Col>
          <Col md={1} className="d-flex gap-2">
            {showRefreshButton && (
              <Button
                type='button'
                variant='outline-secondary'
                onClick={() => refreshItemHandler(item)}
                disabled={refreshingIsbn === item.isbn || refreshingAll}
                title="Vernieuw prijs"
              >
                <FaSync />
              </Button>
            )}
            <Button
              type='button'
              variant='light'
              onClick={() => removeFromCartHandler(item.isbn)}
            >
              <FaTrash />
            </Button>
          </Col>
        </Row>
      </ListGroup.Item>
    ));

  return (
    <Row>
      <Col md={10}>
        <h2 className="mb-4 text-center">Uw Winkelwagen</h2>
        {cartItems.length === 0 ? (
          <Message>
            Uw winkelwagen is leeg. <Link to='/'>Ga terug</Link>
          </Message>
        ) : (
          <>
            <PageHeader
              totalBooks={cartItems.length}
              totalWeight={0}
              totalPrice={totalPrice}
            />
            <Message variant='info'>
              Aangeboden prijs geldt alleen voor de komende 48 uur. Na 48 uur moet elk boek opnieuw worden vernieuwd voordat het kan worden verzonden.
            </Message>
            {feedback && (
              <Message variant={feedback.includes('Geen prijs') || feedback.includes('verwijder') ? 'danger' : 'success'}>{feedback}</Message>
            )}
            {hasPendingItems && (
              <Message variant='warning'>Items in de rode of oranje lijst moeten eerst worden opgelost.</Message>
            )}

            {redItems.length > 0 && (
              <div className="mb-4">
                <h5 className="text-danger">Rode lijst – prijs ontbreekt</h5>
                <p className="text-muted small mb-2">Deze items moeten worden verwijderd of opnieuw worden opgezocht.</p>
                <ListGroup variant='flush'>{renderItemRows(redItems, true)}</ListGroup>
              </div>
            )}

            {orangeItems.length > 0 && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="text-warning mb-0">Oranje lijst – vernieuwen</h5>
                  <Button
                    type='button'
                    variant='outline-warning'
                    size='sm'
                    onClick={refreshAllStaleItemsHandler}
                    disabled={refreshingAll}
                  >
                    {refreshingAll ? 'Bezig…' : 'Alles vernieuwen'}
                  </Button>
                </div>
                <ListGroup variant='flush'>{renderItemRows(orangeItems, true)}</ListGroup>
              </div>
            )}

            {greenItems.length > 0 && (
              <div className="mb-4">
                <h5 className="text-success">Groene lijst – up to date</h5>
                <ListGroup variant='flush'>{renderItemRows(greenItems, false)}</ListGroup>
              </div>
            )}
          </>
        )}
      </Col>

      <Col md={2}>
        <Card>
          <ListGroup variant='flush'>
            <ListGroup.Item>
              <h4>Totaal</h4>
              <h6>{cartItems.length} boeken</h6>
              <h5>€ {totalPrice}</h5>
            </ListGroup.Item>
            <ListGroup.Item>
              <Button
                type='button'
                className='btn-block w-100'
                disabled={cartItems.length === 0 || hasPendingItems}
                onClick={checkoutHandler}
              >
                Verzenden
              </Button>
            </ListGroup.Item>
            <ListGroup.Item>
              <Button
                type='button'
                className='btn-block w-100'
                style={{ backgroundColor: '#28a745', borderColor: '#28a745', color: 'white' }}
                variant='secondary'
                onClick={() => navigate('/')}
              >
                Nog toevoegen
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default CartScreen;